import { Box, Flex, Text } from '@chakra-ui/react'
import { COLORS, ColorTokens } from '@ds/tokens/legacy/colors'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GetIcon, Icons } from 'ui'

import { TEXT_ENUM } from '@/styles/components/text'

import { SidebarOptionType } from '../hooks/useSidebarOptions'
import { SidebarNavItemSuboptions } from './SidebarNavItemSuboptions'

const ICON_CONTAINER_WIDTH = '36px'

interface Props extends SidebarOptionType {
	isHovered: boolean
	isFullyExpanded: boolean
	badge?: number
	onClick?: (e: React.MouseEvent) => void
	onMouseEnter?: () => void
}

export const SidebarNavItem = ({
	title,
	isHovered,
	isFullyExpanded,
	navTo,
	icon,
	suboptions,
	isActive,
	isNewTab,
	badge,
	isBeta,
	onClick,
	onMouseEnter,
}: Props) => {
	const navigate = useNavigate()
	const anyChildActive = suboptions?.some((suboption) => suboption.isActive)
	const showActiveStyle = isHovered ? isActive && !anyChildActive : isActive
	const hasSuboptions = suboptions && suboptions.length > 0
	const [isSubmenuOpen, setIsSubmenuOpen] = useState(isActive)
	const isExternalLink = isNewTab || navTo.startsWith('http')

	const getFullPath = (path: string): string => {
		if (path.startsWith('http') || path.startsWith('https')) {
			return path
		}

		if (path.startsWith('/')) {
			return path
		}

		return `/${path}`
	}

	const fullPath = getFullPath(navTo)

	useEffect(() => {
		if (!isHovered || !isFullyExpanded) {
			setIsSubmenuOpen(false)
		} else if (
			isHovered &&
			isFullyExpanded &&
			anyChildActive &&
			hasSuboptions &&
			!isSubmenuOpen
		) {
			setIsSubmenuOpen(true)
		}
	}, [isHovered, isFullyExpanded, isSubmenuOpen, anyChildActive, hasSuboptions])

	useEffect(() => {
		if (isActive !== undefined) {
			setIsSubmenuOpen(isActive)
		}
	}, [isActive])

	const handleClick = (e: React.MouseEvent) => {
		if (hasSuboptions) {
			if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
				e.preventDefault()
				setIsSubmenuOpen(!isSubmenuOpen)
			}
		} else if (!isExternalLink) {
			if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
				if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
					e.preventDefault()
					navigate(navTo)
				}
			}
		}
	}

	const suboptionsComponent = (
		<SidebarNavItemSuboptions
			suboptions={suboptions}
			isActive={isSubmenuOpen && isFullyExpanded}
			isFullyExpanded={isFullyExpanded}
			onMouseEnter={onMouseEnter}
		/>
	)

	const content = (
		<Flex
			w={isHovered ? 'full' : ICON_CONTAINER_WIDTH}
			h="36px"
			position="relative"
			borderRadius="10px"
			bg={showActiveStyle ? ColorTokens.card_secondary : 'transparent'}
			_hover={{
				bg: ColorTokens.card_secondary,
			}}
			userSelect="none"
			onMouseEnter={onMouseEnter}
			transition="width 250ms ease-in-out, background-color 200ms ease-in-out"
		>
			<Flex
				position="absolute"
				left="0"
				top="0"
				w={ICON_CONTAINER_WIDTH}
				h="36px"
				alignItems="center"
				justifyContent="center"
				userSelect="none"
			>
				<GetIcon
					fontSize="16px"
					icon={icon}
					color={showActiveStyle ? COLORS.background[2] : COLORS.background[5]}
				/>
			</Flex>

			{isHovered && isFullyExpanded && (
				<Flex
					h="100%"
					alignItems="center"
					ml={ICON_CONTAINER_WIDTH}
					color={COLORS.background[2]}
					justifyContent="space-between"
					pr={2}
					w="calc(100% - 36px)"
					opacity={1}
					transition="opacity 250ms ease-in-out"
					animation="fadeIn 250ms ease-in-out"
					userSelect="none"
					sx={{
						'@keyframes fadeIn': {
							'0%': { opacity: 0 },
							'100%': { opacity: 1 },
						},
					}}
				>
					<Text variant={TEXT_ENUM.medium14} userSelect="none">
						{title}
						{isBeta && (
							<Text
								as="sup"
								fontSize="xs"
								color={COLORS.purple[9]}
								fontWeight="bold"
								ml={1}
							>
								βeta
							</Text>
						)}
					</Text>

					{hasSuboptions && (
						<GetIcon
							fontSize="12px"
							icon={Icons.chevron_down}
							color={COLORS.background[5]}
							transition="transform 250ms cubic-bezier(0.4, 0, 0.2, 1)"
							transform={isSubmenuOpen ? 'rotate(180deg)' : 'rotate(0)'}
							ml={1}
							flexShrink={0}
						/>
					)}
				</Flex>
			)}

			{badge !== undefined && badge > 0 && (
				<Flex
					position="absolute"
					top={isHovered ? '10px' : '-2px'}
					right={isHovered ? '12px' : '-2px'}
					minW="16px"
					px={0.5}
					borderRadius="4px"
					bg={COLORS.purple[9]}
					alignItems="center"
					justifyContent="center"
					userSelect="none"
				>
					<Text variant={TEXT_ENUM.bold12} color="white" userSelect="none">
						{badge > 9 ? '9+' : badge}
					</Text>
				</Flex>
			)}
		</Flex>
	)

	return (
		<Flex w="full" direction="column">
			{onClick && (
				<Box as="button" w="full" onClick={onClick} userSelect="none">
					{content}
				</Box>
			)}
			{!onClick && (
				<Box
					as="a"
					w="full"
					href={fullPath}
					target={isExternalLink ? '_blank' : undefined}
					rel={isExternalLink ? 'noopener noreferrer' : undefined}
					onClick={handleClick}
					_hover={{
						textDecoration: 'none',
					}}
					userSelect="none"
				>
					{content}
				</Box>
			)}
			{suboptionsComponent}
		</Flex>
	)
}
